import { Link } from 'react-router-dom';
import Footer from '../../home/components/Footer';

export default function BusinessDeductionsArticle() {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img
              src="https://static.readdy.ai/image/8dbd60c8f218d42716666e4ddaa548a8/e10d3fd4f000823b1408934f8a9fe8ab.png"
              alt="AMM Management Group"
              className="h-12"
            />
          </Link>
          <Link
            to="/#resources"
            className="text-sm text-[#0a1628] hover:text-[#00e5c7] transition-colors font-semibold"
          >
            ← Back to Resources
          </Link>
        </div>
      </nav>

      {/* Article Content */}
      <article className="pt-32 pb-24 px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-12">
            <span className="inline-block px-3 py-1 bg-[#00e5c7] text-[#0a1628] text-xs uppercase tracking-wider rounded-md font-semibold mb-4">
              DEDUCTIONS
            </span>
            <h1
              className="text-4xl md:text-5xl font-bold text-[#0a1628] mb-6 leading-tight"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Maximizing Business Deductions: What You Need to Know
            </h1>
            <div className="flex items-center text-[#5a6a7e] text-sm">
              <span>March 15, 2024</span>
              <span className="mx-3">•</span>
              <span>8 min read</span>
            </div>
          </div>

          {/* Featured Image */}
          <div className="w-full aspect-[16/9] rounded-lg overflow-hidden mb-12">
            <img
              src="https://public.readdy.ai/ai/img_res/906fba1a51792551572587cab9de1585.jpg"
              alt="Business Deductions"
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* Article Body */}
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-[#5a6a7e] leading-relaxed mb-6">
              Understanding and maximizing business deductions is one of the most effective ways to reduce your tax liability. Many business owners miss out on valuable deductions simply because they're unaware of what qualifies. This comprehensive guide will help you identify and claim all the deductions you're entitled to.
            </p>

            <h2 className="text-2xl font-bold text-[#0a1628] mt-12 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Common Business Deductions
            </h2>
            <p className="text-[#5a6a7e] leading-relaxed mb-6">
              The IRS allows businesses to deduct ordinary and necessary expenses incurred in the course of operating your business. Here are the most common categories:
            </p>

            <h3 className="text-xl font-bold text-[#0a1628] mt-8 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              1. Home Office Deduction
            </h3>
            <p className="text-[#5a6a7e] leading-relaxed mb-4">
              If you use part of your home exclusively and regularly for business, you may qualify for the home office deduction. You can choose between the simplified method ($5 per square foot, up to 300 square feet) or the regular method (actual expenses based on the percentage of your home used for business).
            </p>

            <h3 className="text-xl font-bold text-[#0a1628] mt-8 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              2. Vehicle and Travel Expenses
            </h3>
            <p className="text-[#5a6a7e] leading-relaxed mb-4">
              Business use of your vehicle is deductible. For 2024, the standard mileage rate is 67 cents per mile. Alternatively, you can deduct actual expenses like gas, insurance, repairs, and depreciation based on the percentage of business use. Don't forget to track business travel expenses including airfare, hotels, and 50% of business meals.
            </p>

            <h3 className="text-xl font-bold text-[#0a1628] mt-8 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              3. Equipment and Supplies
            </h3>
            <p className="text-[#5a6a7e] leading-relaxed mb-4">
              Office supplies, computers, software, and equipment are all deductible. Under Section 179, you can deduct up to $1,160,000 in equipment purchases in 2024, rather than depreciating them over several years. This is particularly beneficial for small businesses making significant equipment investments.
            </p>

            <h3 className="text-xl font-bold text-[#0a1628] mt-8 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              4. Professional Services
            </h3>
            <p className="text-[#5a6a7e] leading-relaxed mb-4">
              Fees paid to accountants, attorneys, consultants, and other professional services are fully deductible. This includes tax preparation fees, legal advice, and business consulting services.
            </p>

            <h2 className="text-2xl font-bold text-[#0a1628] mt-12 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Often Overlooked Deductions
            </h2>

            <h3 className="text-xl font-bold text-[#0a1628] mt-8 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Employee Benefits
            </h3>
            <p className="text-[#5a6a7e] leading-relaxed mb-4">
              Health insurance premiums, retirement plan contributions, and other employee benefits are deductible. Self-employed individuals can deduct health insurance premiums for themselves and their families as an adjustment to income.
            </p>

            <h3 className="text-xl font-bold text-[#0a1628] mt-8 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Education and Training
            </h3>
            <p className="text-[#5a6a7e] leading-relaxed mb-4">
              Costs for education and training that maintain or improve skills required in your business are deductible. This includes seminars, workshops, professional development courses, and business-related books and subscriptions.
            </p>

            <h3 className="text-xl font-bold text-[#0a1628] mt-8 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Marketing and Advertising
            </h3>
            <p className="text-[#5a6a7e] leading-relaxed mb-4">
              All costs related to promoting your business are deductible, including website development and hosting, social media advertising, business cards, brochures, and sponsorships.
            </p>

            <h2 className="text-2xl font-bold text-[#0a1628] mt-12 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Record-Keeping Best Practices
            </h2>
            <p className="text-[#5a6a7e] leading-relaxed mb-6">
              To claim deductions, you must maintain proper documentation. Here are essential record-keeping tips:
            </p>
            <ul className="list-disc pl-6 text-[#5a6a7e] leading-relaxed mb-6 space-y-2">
              <li>Keep all receipts and invoices for business expenses</li>
              <li>Maintain a mileage log for vehicle deductions</li>
              <li>Use separate bank accounts and credit cards for business</li>
              <li>Document the business purpose of expenses</li>
              <li>Store records for at least 7 years</li>
              <li>Consider using accounting software to track expenses automatically</li>
            </ul>

            <h2 className="text-2xl font-bold text-[#0a1628] mt-12 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Conclusion
            </h2>
            <p className="text-[#5a6a7e] leading-relaxed mb-6">
              Maximizing your business deductions requires careful planning and meticulous record-keeping throughout the year. Don't wait until tax season to think about deductions. By staying organized and working with a qualified tax professional, you can ensure you're taking advantage of every deduction available to you, ultimately reducing your tax burden and keeping more money in your business.
            </p>

            <div className="mt-12 p-8 bg-[#0a1628] rounded-lg">
              <h3 className="text-xl font-bold text-white mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Need Help Maximizing Your Deductions?
              </h3>
              <p className="text-white/80 mb-6">
                Our tax professionals can help you identify all eligible deductions and develop a comprehensive tax strategy for your business.
              </p>
              <Link
                to="/#contact"
                className="inline-block px-6 py-3 bg-[#00e5c7] text-[#0a1628] rounded-lg font-semibold hover:bg-[#00e5c7]/90 transition-colors whitespace-nowrap"
              >
                Schedule a Consultation
              </Link>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}
